// Submissões de todas as equipes, com filtros e paginação.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Send, ChevronLeft, ChevronRight, FilterX } from "lucide-react";
import { LANGUAGES, SUBMISSION_STATUSES, VERDICTS } from "@compila/shared";
import type { AdminProblemDto, AdminSubmissionPageDto, TeamDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { formatContestTime, formatMemory, formatTime } from "../../lib/format.js";
import { Button } from "../../components/motion/button/base";
import { Table, type TableColumn } from "../../components/motion/table";
import { AlertMessage } from "../../components/alerts";
import {
  EmptyState,
  PageHeader,
  VerdictBadge,
  tableHeight,
} from "../../components/domain.js";
import { Identicon } from "../../components/Identicon.js";

const PAGE_SIZE = 50;

type Row = AdminSubmissionPageDto["items"][number];

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível carregar as submissões.";

export function AdminSubmissionsPage(): React.JSX.Element {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);

  const teams = useQuery({
    queryKey: ["admin-teams"],
    queryFn: () => api.get<TeamDto[]>("/admin/teams"),
  });
  const problems = useQuery({
    queryKey: ["admin-problems"],
    queryFn: () => api.get<AdminProblemDto[]>("/admin/problems"),
  });

  const query = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(offset),
  });
  for (const [k, v] of Object.entries(filters)) if (v) query.set(k, v);

  const submissions = useQuery({
    queryKey: ["admin-submissions", query.toString()],
    queryFn: () => api.get<AdminSubmissionPageDto>(`/admin/submissions?${query}`),
  });

  const apply = (key: string, value: string): void => {
    setFilters((f) => ({ ...f, [key]: value }));
    setOffset(0);
  };

  const hasFilters = Object.values(filters).some(Boolean);
  const total = submissions.data?.total ?? 0;
  const items = submissions.data?.items ?? [];

  const columns: TableColumn<Row>[] = [
    {
      key: "contestSeconds",
      header: "Hora",
      width: "100px",
      cell: (s) => (
        <span className="font-mono tabular-nums text-muted-foreground">
          {formatContestTime(s.contestSeconds)}
        </span>
      ),
    },
    {
      key: "teamName",
      header: "Equipe",
      // Coluna flexivel da tabela: absorve o espaco que sobraria na
      // coluna-preenchimento do colgroup.
      cell: (s) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <Identicon seed={s.teamId} className="size-7" />
          <span className="truncate font-medium" title={s.teamName}>
            {s.teamName}
          </span>
        </div>
      ),
    },
    {
      key: "problemLabel",
      header: "Prob.",
      width: "70px",
      align: "center",
      cell: (s) => (
        <span className="rounded-md border border-border px-1.5 py-0.5 text-xs font-medium">
          {s.problemLabel}
        </span>
      ),
    },
    {
      key: "language",
      header: "Linguagem",
      width: "110px",
      cell: (s) => <span className="text-xs text-muted-foreground">{s.language}</span>,
    },
    {
      key: "verdict",
      header: "Veredito",
      width: "150px",
      cell: (s) => <VerdictBadge status={s.status} verdict={s.verdict} />,
    },
    {
      key: "timeMs",
      header: "Tempo",
      width: "90px",
      align: "right",
      cell: (s) => (
        <span className="tabular-nums text-muted-foreground">{formatTime(s.timeMs)}</span>
      ),
    },
    {
      key: "memoryKb",
      header: "Memória",
      width: "90px",
      align: "right",
      cell: (s) => (
        <span className="tabular-nums text-muted-foreground">
          {formatMemory(s.memoryKb)}
        </span>
      ),
    },
    {
      key: "judgeAttempts",
      header: "Tent.",
      width: "60px",
      align: "center",
      cell: (s) => <span className="text-muted-foreground">{s.judgeAttempts}</span>,
    },
    {
      key: "actions",
      header: "",
      width: "80px",
      align: "right",
      cell: (s) => (
        <Link
          to={`/admin/submissoes/${s.id}`}
          className="text-xs text-accent hover:underline"
        >
          abrir
        </Link>
      ),
    },
  ];

  return (
    <div className="space-y-7">
      <PageHeader
        title="Submissões"
        description="Todo o histórico da competição. Abra uma submissão para ver o código e o resultado por caso de teste."
      />

      <section className="flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-card p-5">
        <Filter
          label="Equipe"
          value={filters.teamId ?? ""}
          onChange={(v) => apply("teamId", v)}
        >
          <option value="">todas</option>
          {(teams.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Filter>

        <Filter
          label="Problema"
          value={filters.problemId ?? ""}
          onChange={(v) => apply("problemId", v)}
        >
          <option value="">todos</option>
          {(problems.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}. {p.title}
            </option>
          ))}
        </Filter>

        <Filter
          label="Veredito"
          value={filters.verdict ?? ""}
          onChange={(v) => apply("verdict", v)}
        >
          <option value="">todos</option>
          {VERDICTS.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </Filter>

        <Filter
          label="Status"
          value={filters.status ?? ""}
          onChange={(v) => apply("status", v)}
        >
          <option value="">todos</option>
          {SUBMISSION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Filter>

        <Filter
          label="Linguagem"
          value={filters.language ?? ""}
          onChange={(v) => apply("language", v)}
        >
          <option value="">todas</option>
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </Filter>

        {hasFilters && (
          <Button
            variant="secondary"
            className="rounded-xl"
            onClick={() => {
              setFilters({});
              setOffset(0);
            }}
          >
            <FilterX size={15} aria-hidden />
            Limpar
          </Button>
        )}
      </section>

      {submissions.error && (
        <AlertMessage tone="error">{errorMessage(submissions.error)}</AlertMessage>
      )}

      <section className="overflow-hidden rounded-2xl border border-border bg-card">
        <Table
          data={items}
          columns={columns}
          getRowId={(s) => s.id}
          loading={submissions.isPending}
          rowHeight={60}
          height={tableHeight(items.length, { rowHeight: 60, max: 620 })}
          className="rounded-none border-0 bg-transparent"
          emptyState={
            <EmptyState
              icon={Send}
              title={hasFilters ? "Nada com esses filtros" : "Nenhuma submissão ainda"}
              description={
                hasFilters
                  ? "Ajuste ou limpe os filtros para ver outras submissões."
                  : "As submissões aparecem aqui assim que as equipes começarem a enviar."
              }
            />
          }
        />

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted-foreground">
          <span>
            {total === 0
              ? "Nenhuma submissão"
              : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} de ${total}`}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="rounded-full"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              <ChevronLeft size={14} aria-hidden />
              Anterior
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="rounded-full"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Próxima
              <ChevronRight size={14} aria-hidden />
            </Button>
          </div>
        </footer>
      </section>
    </div>
  );
}

function Filter({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs text-muted-foreground">{label}</span>
      <select
        className="h-10 rounded-xl border border-border bg-background px-3 text-sm outline-none transition-colors focus-visible:border-accent"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {children}
      </select>
    </label>
  );
}
